import { useEffect, useRef, useState } from 'react';
import { Import } from 'assets/icons';
import CheckboxOptionCard from 'components/checkboxOptionCard/CheckboxOptionCard';
import {
  ACCEPT_ATTRIBUTE,
  ACCEPTED_LABEL,
  formatFileSize,
  justificatifError,
} from 'helper/justificatifFile';

export const CONFIRMATION_TITLE = 'Confirmer les modifications';

/**
 * The step between editing a company setting and saving it (JBWEB-1214).
 *
 * These values end up on contracts and invoices, so the change has to carry
 * the client's written request for it. Shown in place of the modal's own body
 * rather than as a second modal: the panel shows one modal at a time, and
 * stacking two would hide the values being confirmed.
 *
 * Nothing is uploaded from here. The file is held until the caller confirms,
 * so abandoning the step leaves nothing stored — which is what the card asks.
 */
const JustificatifConfirmation = ({
  file,
  setFile,
  isAttested,
  setIsAttested,
  error,
  description = 'Ces paramètres ont un impact direct sur le calcul des heures. Un justificatif est requis avant validation.',
}) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const localError = justificatifError(file);

  // Dropping a file anywhere a browser does not expect one makes it navigate
  // to that file, which closes the modal and loses the values being confirmed.
  // Swallowed for as long as this step is open, so a near-miss costs nothing.
  useEffect(() => {
    const swallow = (event) => event.preventDefault();
    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const onDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const dropped = event.dataTransfer?.files?.[0];
    if (dropped) setFile(dropped);
  };

  return (
    <div
      className={'flex flex-col gap-4'}
      onDragEnter={(event) => {
        event.preventDefault();
        setIsDragging(true);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => {
        // Moving onto a child fires dragleave on the parent, so the highlight
        // would flicker without asking where the pointer actually went.
        if (event.currentTarget.contains(event.relatedTarget)) return;
        setIsDragging(false);
      }}
      onDrop={onDrop}
    >
      <div className={'text-gray-500'}>{description}</div>

      <div className={'flex flex-col gap-1'}>
        <div className={'ft-sm text-gray-500'}>Justificatif client</div>
        {/* One button, not a button beside a field: the whole row opens the
            picker, so the empty space next to "Importer" is a target rather
            than dead surface. A single interactive element also keeps the
            markup valid, which nesting one inside the row would not. */}
        <button
          type={'button'}
          className={`flex items-stretch border rounded-md overflow-hidden text-left w-full ${
            isDragging ? 'border-[#F99600] bg-[#FFF7ED]' : 'hover:bg-gray-50'
          }`}
          onClick={() => inputRef.current?.click()}
          title={file?.name}
        >
          <span
            className={
              'flex items-center gap-2 px-3 py-2 border-r shrink-0 bg-white'
            }
          >
            <Import wh={16} color={'#111827'} />
            Importer
          </span>
          {/* The name is the only confirmation the person gets that they
              picked the right file, so it is not truncated away silently. */}
          <span className={'flex items-center px-3 py-2 min-w-0 text-gray-900'}>
            <span className={'truncate'}>
              {file?.name ?? (isDragging ? 'Déposez le fichier ici' : '')}
            </span>
            {file && (
              <span className={'text-gray-400 ft-sm ml-2 shrink-0'}>
                · {formatFileSize(file.size)}
              </span>
            )}
          </span>
        </button>
        <input
          ref={inputRef}
          type={'file'}
          className={'hidden'}
          accept={ACCEPT_ATTRIBUTE}
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        {localError || error ? (
          <div className={'text-red-500 ft-sm'}>{localError ?? error}</div>
        ) : (
          <div className={'text-gray-400 ft-sm'}>
            {ACCEPTED_LABEL} · ou glissez-le dans la fenêtre
          </div>
        )}
      </div>

      <CheckboxOptionCard
        checked={isAttested}
        onChange={(event) => setIsAttested(event.target.checked)}
        title={'Attestation sur l’honneur'}
        description={
          'J’atteste que ces données sont exactes et conformes avec le document fourni.'
        }
      />
    </div>
  );
};

/** Both halves are required: the document alone attests nothing, and the
 *  attestation alone leaves no trace of what the client asked for. */
export const canConfirmJustificatif = (file, isAttested) =>
  Boolean(file) && !justificatifError(file) && isAttested;

export default JustificatifConfirmation;
